import { useQuery } from '@apollo/client';
import NoResultsIcon from '@clearkit/icons/glyphs/NoResults';
import { Link } from 'react-router-dom';

import GraphqlError from '~/components/GraphqlError';
import LoadingArea from '~/components/LoadingArea';
import SearchField from '~/components/SearchTextField';
import Tile from '~/components/SegmentTile';
import TimeAgo from '~/components/TimeAgo';
import paths from '~/lib/paths';

import query from './query.graphql';

const PersonSegments = ({ person: { id } }) => {
  const [search, setSearch] = useState('');

  const { data, error, loading, refetch } = useQuery(query, {
    variables: { id },
    returnPartialData: true,
  });

  if (!data && loading) return <LoadingArea />;

  const {
    person: { segments },
  } = data;

  let filteredSegments = segments;
  if (search) {
    filteredSegments = segments.filter((s) =>
      s.name.toLowerCase().includes(search.toLowerCase()),
    );
  }

  return (
    <div className="flex flex-col flex-grow flex-shrink min-h-0 bg-gray-0">
      <GraphqlError error={error} refetch={refetch} className="mb-4" />
      <div className="flex flex-col flex-shrink min-h-0 px-4 py-6">
        <div className="px-4">
          {segments?.length === 0 ? (
            <div className="flex flex-col items-center pt-8 mx-auto text-center w-xs">
              <NoResultsIcon width={40} height={40} className="text-gray-200" />
              <p className="mt-3 mb-1 text-base text-gray-600">
                This person doesn't appear in any of your audiences.
              </p>
            </div>
          ) : (
            <SearchField
              defaultValue={search}
              onSearch={(e) => setSearch(e.target.value)}
              className="mb-5"
              placeholder="Search for an audience"
            />
          )}
        </div>

        <div className="flex flex-col flex-shrink min-h-0 px-4 py-1 overflow-auto">
          {filteredSegments?.map((segment) => {
            const destinationsAndAudiences = [
              ...segment.destinations,
              ...segment.audiences.edges,
            ];

            return (
              <Link
                to={paths.segment(segment.type, segment.id)}
                key={segment.id}
                className="flex flex-row flex-shrink-0 p-4 mb-4 text-gray-700 bg-white rounded-md ck-box">
                <Tile type={segment.type} className="mr-4" />

                <div className="flex flex-col">
                  <span className="font-semibold">{segment.name}</span>
                  <span>{segment.collection.name}</span>
                </div>

                <div className="flex flex-col ml-auto text-sm leading-relaxed text-right">
                  <span>
                    {destinationsAndAudiences.length > 0 && (
                      <>{destinationsAndAudiences.length} destinations</>
                    )}
                  </span>
                  <span>
                    Updated <TimeAgo time={segment.updatedAt} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

PersonSegments.propTypes = {
  person: PropTypes.shape({
    id: PropTypes.string,
  }),
};

export default PersonSegments;
